import { ApiError, classify, type ApiErrorBody } from "./errors";
import { config } from "../config";
import type { OperatorApplication } from "../domain/operator";
import { EMPTY_APPLICATION } from "../domain/operator";
import { humanRefused, withHuman } from "../auth/human";

/**
 * THE ONE PUBLIC WRITE PATH, and the only backend call in AnkhOS that does NOT
 * go through callGateway.
 *
 * 🔑 IT CANNOT GO THROUGH IT. callGateway FAILS CLOSED without a token and a
 * resolved namespace -- deliberately, because a request whose tenant the server
 * has to guess is a cross-tenant data risk. An applicant has neither: they are
 * not a tenant yet, which is the entire point of applying. Routing this through
 * the choke point would mean weakening the choke point for every authenticated
 * call in order to serve the one call that is legitimately anonymous.
 *
 * So this is a separate function on purpose, and the exception is narrow: one
 * endpoint, one method, no credential, nothing else may use it.
 */

/** POST /access/operator/apply answers 202 with the id and nothing else. */
export interface ApplicationAccepted {
  id: string;
}

/**
 * 422 carries the server's own validation list.
 *
 * 🔑 The server re-validates everything the browser already checked, because
 * this route is public: the form's rules are bypassed by anyone posting JSON.
 * When it disagrees with us its list is the authoritative one, so it is
 * surfaced verbatim rather than replaced with a generic message.
 */
export interface ApplicationRejected {
  error: string;
  problems: string[];
}

export class ApplicationInvalid extends Error {
  readonly problems: string[];
  constructor(message: string, problems: string[]) {
    super(message);
    this.name = "ApplicationInvalid";
    this.problems = problems;
  }
}

/**
 * 🚨 EVERY FIELD IS ACCOUNTED FOR, AND A TEST ENFORCES IT.
 *
 * This form has now silently lost fields TWICE on the way to the backend --
 * six, then eleven -- both times because a field existed on one side and not
 * the other and `encoding/json` discards an unknown key exactly as specified.
 * Listing the ones we send is not enough: the failure was always a field nobody
 * listed.
 *
 * So the split is declared, and intake.test.ts asserts MAPPED ∪ NOT_SENT covers
 * every key of OperatorApplication. Adding a field to the form without deciding
 * where it goes is a test failure, not a silent drop.
 */
export const MAPPED: ReadonlySet<keyof OperatorApplication> = new Set([
  "legalName", "operatorType", "providerKinds", "namespaceGuideRead",
  "requestedNamespace", "upstreamNamespace", "registrationStatus",
  "filingFrequency", "tax", "holdsLicense", "licenseType", "licenseNumber",
  "licensedEntity", "licenseValidFrom", "licenseValidTo", "addressLine1",
  "addressLine2", "city", "state", "pincode", "contactName", "contactEmail",
  "contactMobile", "bankAccountHolder", "bankAccountNumber", "bankIfsc",
  "bankName",
]);

/**
 * Deliberately empty as of v1.47, and that is the point of writing it down.
 *
 * Before v1.47 eleven fields belonged here -- the whole street address and
 * every licence detail but the number -- and nothing recorded that, which is
 * why they were discarded rather than reported. An empty set is a claim that
 * can be checked; an absent set is not.
 */
export const NOT_SENT: ReadonlySet<keyof OperatorApplication> = new Set([]);

/** The wire body. Keys are the CONTRACT and must match radaccess's json tags. */
export interface ApplicationWire {
  requestednamespace: string;
  legalname: string;
  contactname: string;
  contactemail: string;
  contactphone: string;
  operatortype: string;
  providerkinds: string;
  namespaceguideread: boolean;
  bankaccountholder: string;
  bankaccountnumber: string;
  bankifsc: string;
  bankname: string;
  gstin: string;
  pan: string;
  licensenumber: string;
  city: string;
  state: string;
  addressline1: string;
  addressline2: string;
  pincode: string;
  holdslicense: boolean;
  licensetype: string;
  licensedentity: string;
  licensevalidfrom: string;
  licensevalidto: string;
  upstreamnamespace: string;
  registrationstatus: string;
  filingfrequency: string;
}

const taxNum = (a: OperatorApplication, t: string): string =>
  a.tax.find((e) => e.taxtype === t)?.taxnum.trim() ?? "";

/**
 * Two of the twenty-seven are not 1:1, and both are named here rather than
 * inlined, because an unremarked rename is how a field goes missing:
 *
 *   contactMobile -> contactphone   (the names simply differ)
 *   tax[]         -> gstin + pan    (one form field, two wire keys)
 *
 * 🪤 providerKinds is a LIST in the form and a COMMA-SEPARATED STRING on the
 * wire. The server re-normalises (trim, lower-case, de-dup), so this only has
 * to be joined -- but joining it wrong yields a plausible non-empty string, and
 * providerKinds decides whether a licence is required at all.
 */
export function toWire(a: OperatorApplication): ApplicationWire {
  return {
    requestednamespace: a.requestedNamespace.trim(),
    legalname: a.legalName.trim(),
    contactname: a.contactName.trim(),
    contactemail: a.contactEmail.trim(),
    contactphone: a.contactMobile.trim(),
    operatortype: a.operatorType,
    providerkinds: a.providerKinds.join(","),
    namespaceguideread: a.namespaceGuideRead,
    bankaccountholder: a.bankAccountHolder.trim(),
    bankaccountnumber: a.bankAccountNumber.trim(),
    bankifsc: a.bankIfsc.trim(),
    bankname: a.bankName.trim(),
    gstin: taxNum(a, "GSTIN"),
    pan: taxNum(a, "PAN"),
    licensenumber: a.licenseNumber.trim(),
    city: a.city.trim(),
    state: a.state.trim(),
    addressline1: a.addressLine1.trim(),
    addressline2: a.addressLine2.trim(),
    pincode: a.pincode.trim(),
    holdslicense: a.holdsLicense,
    licensetype: a.licenseType,
    licensedentity: a.licensedEntity.trim(),
    licensevalidfrom: a.licenseValidFrom,
    licensevalidto: a.licenseValidTo,
    upstreamnamespace: a.upstreamNamespace.trim(),
    registrationstatus: a.registrationStatus,
    filingfrequency: a.filingFrequency,
  };
}

/** Every key the form has, for the exhaustiveness test. */
export const APPLICATION_KEYS = Object.keys(EMPTY_APPLICATION) as (keyof OperatorApplication)[];

/**
 * Submit an application. Anonymous by design.
 *
 * 🚨 NO Authorization HEADER, and that is not an oversight to be fixed later.
 * radaccess registers this route as ClassPreAuth and it is the only operator
 * route an applicant can reach; every other one requires `operator:*`, which
 * only a platform administrator holds.
 */
export async function submitApplication(
  a: OperatorApplication,
  signal?: AbortSignal,
): Promise<ApplicationAccepted> {
  // 🪤 NO `/access` HERE. gatewayBase ALREADY ENDS IN IT --
  // "https://access.ankhnet.io/access" -- and every other caller passes a path
  // without the prefix (catalog.ts: `${RADGROUP_PREFIX}/radgroup/...`). Writing
  // the full route here yields /access/access/operator/apply, which 404s from
  // nginx with an HTML body, i.e. a failure that looks like an outage rather
  // than a wrong URL. Asserted in intake.test.ts against the shipped
  // config.json value.
  const url = `${config().gatewayBase}/operator/apply`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(withHuman("/operator/apply", toWire(a))),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiError("unavailable", `Could not reach the application service: ${String(cause)}`);
  }

  // 🔑 422 IS NOT A TRANSPORT FAILURE and must not be classified as one.
  // classify() does not map it -- it would fall through to "unavailable", which
  // would tell an applicant the service is down when the service has in fact
  // answered them, in detail, about their own form.
  if (res.status === 422) {
    let body: Partial<ApplicationRejected> = {};
    try {
      body = (await res.json()) as Partial<ApplicationRejected>;
    } catch {
      /* keep the status; the message below still names the cause */
    }
    throw new ApplicationInvalid(
      body.error ?? "The application was not accepted.",
      body.problems ?? [],
    );
  }

  if (!res.ok) {
    let body: Partial<ApiErrorBody> = {};
    try {
      body = (await res.json()) as Partial<ApiErrorBody>;
      humanRefused("/operator/apply", res.status, body);
    } catch {
      /* a non-JSON error body is still an error; keep the status */
    }
    throw new ApiError(
      classify(res.status),
      body.error ?? `HTTP ${res.status} submitting the application`,
      res.status,
    );
  }

  return (await res.json()) as ApplicationAccepted;
}
